import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

import { AjaxService } from './ajax.service';
import { WHO_AM_I } from './account-api.constants';

export interface CurrentUserProfile {
	userRecId: number;
	userName: string;
	firstName: string;
	lastName: string;
	language: string;
	direction: string;
}

const GUEST: CurrentUserProfile = {
	userRecId: 0,
	userName: '',
	firstName: '',
	lastName: '',
	language: '',
	direction: ''
};

// Holds the current user's identity in memory only - NOT localStorage. The backend's
// HttpOnly session cookie is the real source of truth; this service just caches what
// GET /api/login/me last told us, synchronously, so the ~113 call sites of
// ViewScopeService.getCurrentOpertaingUserId() don't need to become async.
@Injectable({
	providedIn: 'root'
})
export class AuthStateService {
	private current: CurrentUserProfile = { ...GUEST };
	private ready$: Observable<void> | null = null;

	constructor(private ajaxService: AjaxService) { }

	getCurrentUserId(): number {
		return this.current.userRecId;
	}

	getUserName(): string {
		return this.current.userName;
	}

	getFirstName(): string {
		return this.current.firstName;
	}

	getLastName(): string {
		return this.current.lastName;
	}

	getLanguage(): string {
		return this.current.language;
	}

	getDirection(): string {
		return this.current.direction;
	}

	setCurrentUser(profile: CurrentUserProfile): void {
		this.current = { ...profile };
	}

	clear(): void {
		this.current = { ...GUEST };
	}

	// Restores identity from the session cookie alone (GET /api/login/me). Bootstrap
	// (provideAppInitializer in app.config.ts) starts this, but the router's initial
	// navigation does NOT reliably wait for it - AuthGuard/HomeRedirectGuard call
	// ensureInitialized() below instead of trusting bootstrap ordering.
	private initializeFromServer(): Observable<void> {
		const url = this.ajaxService.getBaseOfApi() + WHO_AM_I;

		return (this.ajaxService.getData(url) as Observable<any[]>).pipe(
			map(response => {
				const row = Array.isArray(response) ? response[0] : response;
				if (row && Number(row.RESULT) > 0) {
					this.setCurrentUser({
						userRecId: Number(row.RESULT),
						userName: row.UserName ?? '',
						firstName: row.FirstName ?? '',
						lastName: row.LastName ?? '',
						language: row.Language ?? '',
						direction: row.Direction ?? ''
					});
				} else {
					this.clear();
				}
			}),
			catchError(() => {
				// No valid session cookie (guest, or it expired/was revoked) - not an error
				// from the app's point of view, just means "not logged in".
				this.clear();
				return of(void 0);
			})
		);
	}

	// Single in-flight/cached session check, shared by the app initializer and both
	// guards - whichever asks first triggers the one GET /api/login/me call, everyone
	// else (same tick or later) gets the same completion via shareReplay(1).
	ensureInitialized(): Observable<void> {
		if (!this.ready$) {
			this.ready$ = this.initializeFromServer().pipe(shareReplay(1));
		}
		return this.ready$;
	}
}
